import { Box, Checkbox, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { format, parse } from 'date-fns';
import { sortBy } from 'lodash-es';
import { useMemo, useRef } from 'react';

import { Dictionary } from '@calo/types';

import { caloTheme } from '../../../../assets/themes/calo';
import { CookingStationName, FoodComponent } from '../../../../libs';
import { getTableAttributes } from '../../../../libs/utils/helperFunctions';
import { ViewTypes } from '../../types';
import Section from '../Section';

const NO_SELECTION = new Set<string>();

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.neutral50,
    color: theme.palette.common.black
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}));

const STICKY_CHECKBOX_WIDTH = 40;
const STICKY_NAME_WIDTH = 220;
const STICKY_STATIONS_WIDTH = 140;

export interface SectionTableProps {
  tableName: string;
  foodComponents: FoodComponent[];
  shortages: Dictionary<{
    station: string;
    total: number;
  }>;
  highlightedComponentId?: string | null;
  onHighlightEnd?: () => void;
  showAllUnits: boolean;
  selectedHours: string;
  selectedIdsSet?: Set<string>;
  onComponentSelection?: (component: FoodComponent, isSelected: boolean) => void;
  isTablet?: boolean;
}

const SectionTable = ({
  tableName,
  foodComponents,
  shortages,
  highlightedComponentId,
  onHighlightEnd,
  showAllUnits,
  selectedHours,
  selectedIdsSet = NO_SELECTION,
  onComponentSelection,
  isTablet = false
}: SectionTableProps) => {
  const theme = useTheme();
  const tableAttributes = getTableAttributes(tableName, theme);
  const myRef = useRef<null | HTMLDivElement>(null);

  const fixFirstTwoColumns = isTablet && tableName === ViewTypes.OVERVIEW;

  const cellTextStyle = {
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'left',
    lineHeight: '24px'
  };

  const cellTextStyleCentered = {
    ...cellTextStyle,
    textAlign: 'center'
  };

  const sortedComponents = useMemo(() => {
    const sorted = sortBy(foodComponents, 'name.en');
    const idx = highlightedComponentId ? sorted.findIndex((fc) => fc.id === highlightedComponentId) : -1;
    if (idx === -1) return sorted;
    const [highlighted] = sorted.splice(idx, 1);
    return [highlighted, ...sorted];
  }, [foodComponents, highlightedComponentId]);

  const allSelected = sortedComponents.length > 0 && sortedComponents.every((fc) => selectedIdsSet.has(fc.id));

  const someSelected = sortedComponents.some((fc) => selectedIdsSet.has(fc.id)) && !allSelected;

  const handleSelectAll = () => {
    if (!onComponentSelection) return;

    if (allSelected) {
      for (const fc of sortedComponents) {
        onComponentSelection(fc, false);
      }
    } else {
      for (const fc of sortedComponents) {
        const isAlreadySelected = selectedIdsSet.has(fc.id);
        if (!isAlreadySelected) {
          onComponentSelection(fc, true);
        }
      }
    }
  };

  const stickyCheckboxSx = fixFirstTwoColumns
    ? {
        position: 'sticky' as const,
        left: 0,
        zIndex: 3,
        backgroundColor: theme.palette.neutral50,
        minWidth: STICKY_CHECKBOX_WIDTH,
        width: STICKY_CHECKBOX_WIDTH,
        boxShadow: '2px 0 4px rgba(0,0,0,0.06)'
      }
    : {};

  const stickyNameSx = fixFirstTwoColumns
    ? {
        position: 'sticky' as const,
        left: onComponentSelection ? STICKY_CHECKBOX_WIDTH : 0,
        zIndex: 3,
        backgroundColor: theme.palette.neutral50,
        minWidth: STICKY_NAME_WIDTH,
        boxShadow: '2px 0 4px rgba(0,0,0,0.06)'
      }
    : {};

  const stickyStationsSx = fixFirstTwoColumns
    ? {
        position: 'sticky' as const,
        left: (onComponentSelection ? STICKY_CHECKBOX_WIDTH : 0) + STICKY_NAME_WIDTH,
        zIndex: 3,
        backgroundColor: theme.palette.neutral50,
        minWidth: STICKY_STATIONS_WIDTH,
        boxShadow: '4px 0 6px rgba(0,0,0,0.08)'
      }
    : {};

  return (
    <Box
      ref={myRef}
      sx={{
        display: tableName === CookingStationName.other && foodComponents.length === 0 ? 'none' : '',
        overflow: 'hidden',
        height: '100%'
      }}
    >
      <TableContainer sx={{ height: '100%' }}>
        <Table
          stickyHeader
          sx={{
            border: '2px solid white',
            transition: 'filter 0.3s ease',
            width: '100%',
            position: 'relative'
          }}
        >
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 5,
              backgroundColor: theme.palette.background.paper,
              isolation: 'isolate',
              height: '80px',
              py: '16px'
            }}
          >
            <TableRow sx={{ minWidth: '600px', cursor: 'pointer' }}>
              {onComponentSelection && (
                <StyledTableCell padding="none" sx={{ px: '10px', width: '40px', textAlign: 'center', ...stickyCheckboxSx }}>
                  <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll} sx={{ padding: 0 }} />
                </StyledTableCell>
              )}
              <StyledTableCell
                padding="none"
                colSpan={onComponentSelection ? 2 : 3}
                sx={{ px: '10px', color: theme.palette.primary900, ...stickyNameSx }}
              >
                <Stack
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start'
                  }}
                >
                  <Typography sx={cellTextStyle}>{tableAttributes.name}</Typography>
                  {shortages[tableName] && shortages[tableName].total > 0 && (
                    <Stack
                      sx={{
                        display: 'flex',
                        flexDirection: 'row'
                      }}
                    >
                      <Typography>Shortage Reported</Typography>
                      <Stack sx={{ backgroundColor: 'red', ml: '6px', borderRadius: '15px' }}>
                        <Typography sx={{ color: 'white', mx: '6px' }}>{shortages[tableName].total}</Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </StyledTableCell>
              {tableName === ViewTypes.OVERVIEW && (
                <StyledTableCell
                  padding="none"
                  colSpan={2}
                  sx={{ px: '10px', color: theme.palette.primary900, minWidth: '120px', ...stickyStationsSx }}
                >
                  <Typography sx={cellTextStyle}>Sections</Typography>
                </StyledTableCell>
              )}
              <StyledTableCell
                padding="none"
                colSpan={2}
                sx={{ px: '10px', color: theme.palette.primary900, minWidth: '120px', textAlign: 'center' }}
              >
                <Typography sx={cellTextStyleCentered}>Total Raw Weight</Typography>
              </StyledTableCell>

              <StyledTableCell
                padding="none"
                colSpan={2}
                sx={{ px: '10px', color: theme.palette.primary900, minWidth: '120px', textAlign: 'center' }}
              >
                <Typography sx={cellTextStyleCentered}>{showAllUnits ? 'Total Quantity' : 'Total Cooked Weight'}</Typography>
              </StyledTableCell>

              <StyledTableCell
                padding="none"
                colSpan={2}
                sx={{ px: '10px', color: theme.palette.primary900, minWidth: '150px', textAlign: 'center' }}
              >
                <Typography sx={cellTextStyleCentered}>
                  Buffer-Adjusted
                  <br />
                  Total Cooked Weight
                </Typography>
              </StyledTableCell>

              {selectedHours && (
                <StyledTableCell
                  padding="none"
                  colSpan={2}
                  sx={{ px: '10px', color: theme.palette.primary900, minWidth: '120px', textAlign: 'center' }}
                >
                  <Typography sx={cellTextStyleCentered}>
                    Changes from {format(parse(selectedHours, 'dd-MMM-yyyy h:mm a', new Date()), 'h:mm a')}
                  </Typography>
                </StyledTableCell>
              )}
              <StyledTableCell
                padding="none"
                colSpan={2}
                sx={{ px: '10px', color: theme.palette.primary900, minWidth: '180px', textAlign: 'center' }}
              >
                <Typography sx={cellTextStyleCentered}>Actual Cooked Weight</Typography>
              </StyledTableCell>

              <StyledTableCell
                padding="none"
                colSpan={2}
                sx={{ px: '10px', color: theme.palette.primary900, minWidth: '180px', textAlign: 'center' }}
              >
                <Typography sx={cellTextStyleCentered}>Expected Shortage/Wastage</Typography>
              </StyledTableCell>

              <StyledTableCell
                padding="none"
                colSpan={4}
                sx={{ px: '10px', color: theme.palette.primary900, textAlign: 'center' }}
              >
                <Typography sx={cellTextStyleCentered}>Status</Typography>
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedComponents.length > 0 ? (
              sortedComponents.map((fc) => (
                <Section
                  key={fc.id}
                  foodComponent={fc}
                  tableName={tableName as unknown as CookingStationName}
                  highlightedComponentId={highlightedComponentId}
                  onHighlightEnd={onHighlightEnd}
                  showAllUnits={showAllUnits}
                  selectedHours={selectedHours}
                  selectedIdsSet={selectedIdsSet}
                  onComponentSelection={onComponentSelection}
                  fixFirstTwoColumns={fixFirstTwoColumns}
                  hasCheckboxColumn={Boolean(onComponentSelection)}
                />
              ))
            ) : (
              <TableRow hover>
                <TableCell
                  colSpan={4}
                  sx={{
                    minWidth: 150,
                    p: 1,
                    fontFamily: caloTheme.typography.fontFamily,
                    fontSize: '28px',
                    textTransform: 'uppercase',
                    color: theme.palette.neutral600,
                    textAlign: 'center',
                    border: 'none'
                  }}
                >
                  No components
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SectionTable;
